import { reduce } from "./machine";
import { type ActionEvent, type Attempt, isPendingStatus } from "./types";

/**
 * The action store: module-level, deliberately outside React.
 *
 * Living outside the tree is the point. Optimistic pending that lives in a
 * component's `useState` dies on unmount, so navigating away from Proposal
 * Summaries and back makes a still-running job look like it never started —
 * and the user clicks Regenerate again. Here the attempt outlives the screen
 * that started it, and any component anywhere reads the same answer.
 *
 * Two things this file owns that a reducer can't:
 *
 * 1. **Teardown.** Everything an attempt holds — its poll registration, its
 *    slow timer — is released together the moment the attempt leaves the
 *    pending set, for ANY reason. Never in an event callback: fencing drops a
 *    superseded attempt's callbacks by design, so a callback-owned cleanup
 *    leaks exactly when it matters most.
 * 2. **Observation.** One shared poll loop covers every pending attempt rather
 *    than an observer each, backs off as attempts age, and catches up
 *    immediately on focus/reconnect instead of waiting out a throttled timer.
 */

/** Poll cadence. Widens with attempt age; a ten-minute job must not cost 300 requests. */
const POLL_STEPS_MS = [2_000, 2_000, 3_000, 5_000, 8_000, 15_000, 30_000];
const POLL_MAX_MS = 30_000;

export interface Watcher {
  /**
   * Fetch this attempt's truth and dispatch whatever it implies. Owned by the
   * attempt, not by a screen: it must work with no relevant page mounted.
   */
  refresh: () => Promise<void>;
}

type Listener = () => void;

interface StoreState {
  attempts: Map<string, Attempt>;
}

const state: StoreState = { attempts: new Map() };
const listeners = new Set<Listener>();
const watchers = new Map<string, Watcher>();
const teardowns = new Map<string, Array<() => void>>();
/**
 * Per-key attempt counter, kept ACROSS eviction. Attempt ids have to be
 * monotonic for the whole life of the tab, not just the life of one attempt:
 * an evicted attempt's in-flight request can still land afterwards, and if the
 * replacement restarted the numbering that stale callback would fence as
 * current and settle the wrong run.
 */
const generations = new Map<string, number>();

/**
 * Effective viewer. Impersonation changes at runtime with no reload, and every
 * request picks up the new identity — so an unscoped store would show one
 * viewer's in-flight work on the next viewer's screen while its poll kept
 * running as somebody else.
 */
let viewerId = "anonymous";

/**
 * Bumped whenever the store is reset — which is what a viewer change does.
 *
 * `resetAll` can clear state, but it cannot un-attach a `.then` already
 * chained onto a serialized tail. Without this, a queued rename fires after
 * the first one settles, sends under the NEW effective viewer, and records its
 * attempt under the old viewer's (now invisible) key.
 */
let viewerGeneration = 0;

/** Bumped on every change so `useSyncExternalStore` can use one cheap snapshot. */
let version = 0;

let pollTimer: ReturnType<typeof setTimeout> | null = null;
/** When the currently-scheduled tick will fire, so a sooner one can replace it. */
let pollTimerDueAt: number | null = null;
/**
 * Per-watcher next-due time. A single shared deadline is not enough: the loop
 * is shared, but the CADENCE is per attempt, and without this a ten-minute job
 * gets polled at a two-second-old attempt's rate — the backoff would be
 * advertised and not delivered.
 */
const dueAt = new Map<string, number>();
let listenersInstalled = false;

export function scopedKey(actionName: string, targetKey: string): string {
  return `${viewerId}::${actionName}:${targetKey}`;
}

function emit(): void {
  version += 1;

  for (const listener of listeners) {
    listener();
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

export function getVersion(): number {
  return version;
}

/** Stable across a server render, where the store is always empty. */
export function getServerVersion(): number {
  return 0;
}

interface ActionSnapshot {
  attempts: ReadonlyMap<string, Attempt>;
  optimistic: ReadonlyMap<string, unknown>;
}

const EMPTY_SNAPSHOT: ActionSnapshot = {
  attempts: new Map(),
  optimistic: new Map(),
};
let snapshotVersion = -1;
let snapshot = EMPTY_SNAPSHOT;

/** Render from immutable data so memoized readers track store changes. */
export function getActionSnapshot(): ActionSnapshot {
  if (snapshotVersion !== version) {
    const optimistic = new Map<string, unknown>();

    for (const [key, held] of optimisticValues) {
      if (state.attempts.get(key)?.attemptId === held.attemptId) {
        optimistic.set(key, held.value);
      }
    }

    snapshot = { attempts: new Map(state.attempts), optimistic };
    snapshotVersion = version;
  }

  return snapshot;
}

export function getServerActionSnapshot(): ActionSnapshot {
  return EMPTY_SNAPSHOT;
}

export function getAttempt(key: string): Attempt | null {
  return state.attempts.get(key) ?? null;
}

export function registerTeardown(key: string, fn: () => void): void {
  const existing = teardowns.get(key);

  if (existing) {
    existing.push(fn);
  } else {
    teardowns.set(key, [fn]);
  }
}

export function registerWatcher(key: string, watcher: Watcher): void {
  watchers.set(key, watcher);
  // Due on the shortest cadence, from now. A brand-new attempt must not
  // inherit an older attempt's 30-second deadline: on a page with no interval
  // of its own, that is half a minute of a finished job still reading as
  // "generating".
  dueAt.set(key, Date.now() + POLL_STEPS_MS[0]);
  registerTeardown(key, () => {
    watchers.delete(key);
    dueAt.delete(key);
  });
  ensurePolling();
}

function releaseResources(key: string): void {
  const fns = teardowns.get(key);
  teardowns.delete(key);

  if (!fns) {
    return;
  }

  for (const fn of fns) {
    try {
      fn();
    } catch {
      // A throwing teardown must not strand the rest of them.
    }
  }
}

export function dispatch(key: string, event: ActionEvent): void {
  const before = state.attempts.get(key) ?? null;
  let applied = event;

  if (event.type === "TRIGGER") {
    applied = { ...event, attemptId: nextAttemptId(key) };
  }

  const after = reduce(before, applied);

  if (after === before) {
    return;
  }

  // A TRIGGER over an existing attempt supersedes it; release the old
  // attempt's resources before the new one registers its own.
  const supersededByRetrigger =
    applied.type === "TRIGGER" && before !== null && after !== null;

  if (after === null || supersededByRetrigger) {
    releaseResources(key);
  }

  if (after === null) {
    state.attempts.delete(key);
  } else {
    state.attempts.set(key, after);
  }

  // Settled attempts stop being observed, but stay readable so the UI can show
  // the outcome until the user acts on it.
  if (after && !isPendingStatus(after.status)) {
    releaseResources(key);
  }

  emit();
}

export function evict(key: string): void {
  dispatch(key, { type: "RELEASED" });
}

/**
 * Adopt work that is already running — the same mechanism whether someone else
 * started it or a previous page load did. Idempotent: a key that already has
 * an attempt is left alone, so this is safe to call on every render pass as
 * scopes come into view.
 */
export function adopt(key: string, attempt: Attempt): boolean {
  const existing = state.attempts.get(key);

  if (existing) {
    // Never clobber live work — that would drop its watcher and its identity.
    if (isPendingStatus(existing.status)) {
      return false;
    }

    // A SETTLED entry, though, is just the outcome we're still showing the
    // user. It must not wedge the key: if another admin starts a new run for
    // this target an hour later, the read model shows it pending and we have
    // to be able to pick it up. Only replace when the identity really is a
    // different run, so repeated observations of the same finished run don't
    // resurrect it as pending.
    if (
      existing.identity?.runId &&
      existing.identity.runId === attempt.identity?.runId
    ) {
      return false;
    }

    releaseResources(key);
  }

  state.attempts.set(key, attempt);
  emit();
  // The caller attaches the watcher: the store has no idea how to observe a
  // given feature, and a placeholder here would be torn down by the real one.
  return true;
}

export function pendingKeys(): string[] {
  const keys: string[] = [];

  for (const [key, attempt] of state.attempts) {
    if (isPendingStatus(attempt.status)) {
      keys.push(key);
    }
  }

  return keys;
}

export function setViewer(nextViewerId: string): void {
  if (nextViewerId === viewerId) {
    return;
  }

  viewerId = nextViewerId;
  // Treated exactly like a reload: stop every observer, drop every attempt.
  // Anything else leaks one person's in-flight work into another's screen,
  // which in an impersonation-heavy admin tool is not a rare edge.
  resetAll();
}

export function resetAll(): void {
  viewerGeneration += 1;

  // Safe to iterate live: teardowns touch `watchers`/`teardowns`, not this map.
  for (const key of state.attempts.keys()) {
    releaseResources(key);
  }

  state.attempts.clear();
  watchers.clear();
  teardowns.clear();
  dueAt.clear();
  // Optimistic values are attempt-scoped, so a reset that left them behind
  // would keep asserting the previous viewer's edit over the new viewer's
  // truth — with no attempt left that could ever release it.
  optimisticValues.clear();
  serialTails.clear();
  stopPolling();
  emit();
}

function pollDelayFor(attempt: Attempt, now: number): number {
  const elapsed = now - attempt.startedAt;
  // Step index grows roughly with how long we've been waiting.
  const index = Math.min(
    POLL_STEPS_MS.length - 1,
    Math.floor(elapsed / 15_000),
  );
  return POLL_STEPS_MS[index] ?? POLL_MAX_MS;
}

function nextDelay(): number | null {
  const now = Date.now();
  let soonest: number | null = null;

  for (const [key, attempt] of state.attempts) {
    if (!isPendingStatus(attempt.status)) {
      continue;
    }

    if (!watchers.has(key)) {
      continue;
    }

    const due = dueAt.get(key) ?? now;
    const delay = Math.max(0, due - now);
    soonest = soonest === null ? delay : Math.min(soonest, delay);
  }

  return soonest;
}

async function tick(): Promise<void> {
  pollTimer = null;
  pollTimerDueAt = null;
  const now = Date.now();
  // Snapshot first: a refresh can dispatch and mutate the map underneath us.
  // Only attempts whose OWN interval has elapsed — the timer is shared, the
  // cadence is not.
  const due = [...watchers.entries()].filter(([key]) => {
    const attempt = state.attempts.get(key);

    if (attempt == null || !isPendingStatus(attempt.status)) {
      return false;
    }

    return (dueAt.get(key) ?? 0) <= now;
  });

  for (const [key] of due) {
    const attempt = state.attempts.get(key);

    if (attempt) {
      dueAt.set(key, now + pollDelayFor(attempt, now));
    }
  }

  // NOT awaited as a group. One stalled HTTP request would otherwise hold the
  // whole loop open and stop it ever rescheduling, so every other attempt goes
  // unpolled until a focus/reconnect — and on a page with no interval of its
  // own, finished work reads as generating indefinitely. Each refresh has
  // already had its next due time set above, so firing them and rescheduling
  // immediately is safe.
  for (const [key, watcher] of due) {
    void (async () => {
      try {
        await watcher.refresh();
      } catch (error) {
        // A failed poll is not evidence about the WORK — it must never resolve
        // the attempt in either direction, so a transient error just waits for
        // the next tick. An auth failure is different in kind: the session is
        // gone, so this attempt is permanently unobservable and retrying only
        // produces a stream of unauthorized requests from a page nobody is
        // even looking at. That is exactly what release is for.
        if (isAuthError(error)) {
          dispatch(key, { type: "RELEASED" });
        }
      }
    })();
  }

  ensurePolling();
}

/** 401/403 from either the tRPC error shape or a bare HTTP error. */
function isAuthError(error: unknown): boolean {
  const data = (error as { data?: { code?: string; httpStatus?: number } })
    ?.data;

  if (data?.httpStatus === 401 || data?.httpStatus === 403) {
    return true;
  }

  return data?.code === "UNAUTHORIZED" || data?.code === "FORBIDDEN";
}

function ensurePolling(): void {
  installGlobalListeners();
  const delay = nextDelay();

  if (delay === null) {
    return;
  }

  const target = Date.now() + delay;

  // Replace an existing timer when something is now due sooner than it — the
  // early-return version let a new attempt wait out an old attempt's deadline.
  if (pollTimer !== null) {
    if (pollTimerDueAt !== null && target >= pollTimerDueAt) {
      return;
    }

    clearTimeout(pollTimer);
  }

  pollTimerDueAt = target;
  pollTimer = setTimeout(() => void tick(), delay);
}

function stopPolling(): void {
  if (pollTimer === null) {
    return;
  }

  clearTimeout(pollTimer);
  pollTimer = null;
  pollTimerDueAt = null;
}

/** Reconcile at once rather than waiting out a throttled timer. */
function catchUp(): void {
  stopPolling();
  // Everything is due now: a throttled or backgrounded tab may have skipped
  // several intervals, and the point of catching up is not to wait out
  // another one.
  const now = Date.now();

  for (const key of watchers.keys()) {
    dueAt.set(key, now);
  }

  void tick();
}

function installGlobalListeners(): void {
  if (listenersInstalled) {
    return;
  }

  if (typeof window === "undefined") {
    return;
  }

  listenersInstalled = true;
  window.addEventListener("focus", catchUp);
  window.addEventListener("online", catchUp);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      catchUp();
    }
  });
}

/**
 * Wait until an attempt has left `sent` — i.e. the enqueue has been accepted or
 * refused. Returns false if it is still in flight when the wait expires.
 *
 * Cancel routes succeed as a NO-OP when no run matches, so cancelling before
 * the run exists reports success and the enqueue then creates the run anyway —
 * paid generation continuing behind a UI that says "canceled". Every cancel
 * path must pass through here first.
 *
 * It lives in the store because it was written by hand once, missed on the next
 * surface, and the miss was the same money bug both times.
 */
/**
 * Keys with a press currently executing. Claimed SYNCHRONOUSLY, before any
 * await.
 *
 * The attempt status alone can't serialise presses: reconciling an `unknown`
 * attempt awaits a fetch, and the status stays `unknown` for that whole window
 * — so two clicks both read "unknown", both await, and both go on to enqueue.
 * A guard that yields before it claims is not a guard.
 */
const starting = new Set<string>();

/**
 * Take the next attempt id for a key. Every attempt must draw from here —
 * triggers AND adoptions. An adopted attempt that hard-coded its id would let
 * the next local trigger reuse the same number, and a still-resolving watcher
 * from the adopted run would then pass the fence and settle its replacement.
 */
/**
 * The newest `requestedAt` we have observed for a key, as reported by the
 * SERVER. Used to tell "this run is newer than anything that existed before the
 * press" without ever comparing a server timestamp to a browser one.
 */
const lastSeenRequestedAt = new Map<string, string>();

export function noteObservedRequestedAt(
  key: string,
  requestedAt: string | null | undefined,
): void {
  if (!requestedAt) {
    return;
  }

  // FROZEN while an outcome is unknown. The baseline's whole job is to answer
  // "did anything new appear since the press?", and an ordinary background
  // refetch can observe the very run that lost its response. Advancing to it
  // would make the retry's comparison find that run equal rather than newer,
  // reject it as evidence, and enqueue a duplicate paid run — the baseline
  // erasing the answer it exists to provide.
  if (getAttempt(key)?.status === "unknown") {
    return;
  }

  const prior = lastSeenRequestedAt.get(key);

  if (prior && prior >= requestedAt) {
    return;
  }

  lastSeenRequestedAt.set(key, requestedAt);
}

/** True when this run is newer than anything seen for the key before now. */
export function isNewerThanSeen(
  key: string,
  requestedAt: string | null | undefined,
): boolean {
  if (!requestedAt) {
    return false;
  }

  const prior = lastSeenRequestedAt.get(key);

  // Never seen one: refuse to claim. Being wrong here would tell the user a
  // regeneration happened when it didn't, which is worse than a duplicate.
  if (!prior) {
    return false;
  }

  return requestedAt > prior;
}

export function nextAttemptId(key: string): number {
  const next = (generations.get(key) ?? 0) + 1;
  generations.set(key, next);
  return next;
}

/** Returns false when a press for this key is already executing. */
export function claimStart(key: string): boolean {
  if (starting.has(key)) {
    return false;
  }

  starting.add(key);
  return true;
}

export function releaseStart(key: string): void {
  starting.delete(key);
}

/**
 * The value an outstanding attempt is asserting, held HERE rather than written
 * into the query cache.
 *
 * This is §4.8's layering, in the form this app actually needs. Writing
 * optimism into the cache means server truth underneath has been overwritten,
 * so undoing it needs a snapshot — and a snapshot taken while another
 * attempt's optimism is in the cache restores a value the server rejected. It
 * also can't be undone correctly on SUCCESS, because by then the confirming
 * fetch has already put fresh truth where the snapshot would go.
 *
 * Keeping the value beside the attempt instead means truth is never touched:
 * the view is `truth + whatever the store is holding`, re-derived at render.
 * There is nothing to snapshot, nothing to roll back, and eviction alone
 * removes it — which is what makes "layer lifetime is attempt lifetime" true
 * by construction rather than by remembering to call something.
 */
const optimisticValues = new Map<
  string,
  { attemptId: number; value: unknown }
>();

export function setOptimistic(
  key: string,
  attemptId: number,
  value: unknown,
): void {
  optimisticValues.set(key, { attemptId, value });
  // Released by the store on eviction, never by a callback — a superseded
  // attempt's callbacks are dropped by fencing, so a callback-owned removal
  // leaks exactly when two attempts overlapped.
  registerTeardown(key, () => {
    if (optimisticValues.get(key)?.attemptId === attemptId) {
      optimisticValues.delete(key);
    }
  });
  emit();
}

/**
 * Every outstanding optimistic value for one action, across all its targets.
 *
 * For a screen that has to apply optimism it cannot address by row — a
 * reorder asserts an ORDER over a group, so there is no row to hang it on and
 * no way to enumerate the group keys from the rows themselves.
 */
export function optimisticValuesFor(
  actionName: string,
  /**
   * Restrict to targets starting with this. An action whose key is only
   * unique within a scope (a reorder group, which repeats per round) has to
   * pass the scope, or one round reads the other's outstanding values.
   */
  targetPrefix = "",
): unknown[] {
  const values: unknown[] = [];

  for (const [key, held] of optimisticValues) {
    if (!key.includes(`::${actionName}:${targetPrefix}`)) {
      continue;
    }

    const attempt = state.attempts.get(key);

    if (!attempt || attempt.attemptId !== held.attemptId) {
      continue;
    }

    values.push(held.value);
  }

  return values;
}

/** The optimistic value for the CURRENT attempt on a key, if it has one. */
export function getOptimistic(key: string): unknown {
  const held = optimisticValues.get(key);

  if (!held) {
    return undefined;
  }

  const attempt = state.attempts.get(key);

  if (!attempt || attempt.attemptId !== held.attemptId) {
    return undefined;
  }

  return held.value;
}

/**
 * Per-key request chain for `concurrency: "serialize"`.
 *
 * Local politeness, not a correctness mechanism (§4.8): it stops THIS tab
 * racing itself and can do nothing about a second tab or a second admin. The
 * chain deliberately holds only the tail promise — the queue's whole job is
 * "don't send the next one until the last one settled".
 */
const serialTails = new Map<string, Promise<unknown>>();

export function runSerially<T>(
  key: string,
  /**
   * `waited` is true only when this call actually queued behind another. The
   * caller needs the difference: §4.8 forbids the QUEUE releasing past an
   * unknown outcome, but a press that never waited is not the queue releasing
   * — it is somebody pressing the button again, which is allowed.
   */
  fn: (waited: boolean) => Promise<T>,
): Promise<T | undefined> {
  const waited = serialTails.has(key);
  // Captured now, checked when this link finally runs. A press queued for one
  // viewer must not execute as another.
  const generation = viewerGeneration;
  const run = () => (generation === viewerGeneration ? fn(waited) : undefined);
  const previous = serialTails.get(key) ?? Promise.resolve();
  // Runs whether the previous link resolved or rejected: the chain sequences
  // requests, it does not propagate their outcomes. One failed write must not
  // poison every later write for the key.
  const result = previous.then(run, run);
  // The tail is a settled-either-way view of `result`, so the next caller waits
  // for this one to finish without inheriting its rejection.
  const tail = result.then(
    () => undefined,
    () => undefined,
  );
  serialTails.set(key, tail);
  void tail.then(() => {
    // Only the newest link clears the map. An older link settling later must
    // not drop a queue that has since grown behind it.
    if (serialTails.get(key) === tail) {
      serialTails.delete(key);
    }
  });
  return result.catch(() => undefined);
}

export type CancelReadiness =
  /** Accepted, or work we didn't start — there is something to cancel. */
  | "proceed"
  /** The enqueue is still in flight; cancelling now could miss the run. */
  | "still-sending"
  /** The trigger was refused or already finished — nothing was ever queued. */
  | "nothing-to-cancel";

export async function awaitEnqueueSettled(
  key: string,
  timeoutMs = 15_000,
): Promise<CancelReadiness> {
  const deadline = Date.now() + timeoutMs;

  while (getAttempt(key)?.status === "sent" && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  const status = getAttempt(key)?.status;

  // Never fall through to cancelling anyway: a deadline that gives up and
  // cancels is the same race, just rarer.
  if (status === "sent") {
    return "still-sending";
  }

  // No attempt means the work is server-side — adopted, or another admin's.
  // The envelope is what put a Cancel control on screen, so it is cancellable.
  if (status === undefined) {
    return "proceed";
  }

  // `applying` has an accepted run. `unknown` may have one, and cancelling is
  // the safe direction there — better a no-op than a run nobody meant to keep.
  if (status === "applying" || status === "unknown") {
    return "proceed";
  }

  // failed / succeeded / canceled: the trigger was refused or is already done,
  // so calling the route would no-op and report a cancellation that never
  // happened.
  return "nothing-to-cancel";
}

/** Test seam: the module-level store would otherwise leak between test files. */
export function __resetActionStoreForTests(): void {
  resetAll();
  viewerId = "anonymous";
  version = 0;
}
